// 一键理牌 v2（作者 11-03《掼蛋「一键理牌」功能完整需求规格》）：
//   ① 解析手牌：王、逢人配（红桃级牌）单独放，其余按点数 / 花色统计；
//   ② 穷举全部同花顺候选（4 花色 × 10 个五连窗口，A 可作 1 或 14，不循环），缺口 ≤ 逢人配张数的都算，不找到第一个就停；
//   ③ 对每种逢人配用法（凑某个 / 两个同花顺、扩成更大的炸弹、不用）分别组牌（炸弹不拆：见下方 keepSets 与 sfCandidates）：
//      先锁四大天王、同花顺、炸弹，余牌用回溯搜索求“出完要的手数最少”的划分（钢板、三连对、顺子、三带对、三张、对子、单张）；
//   ④ 打分：−100×手数 +60×炸弹档位和 −15×单张 −10×散对 +5×大牌（王、级牌、A）−8×拆炸；
//   ⑤ 分数最高的是默认方案，分差 ≤ 20 的作为候选方案给玩家切换。
// 与规格的出入（按《竞技掼蛋竞赛规则》，引擎 rules.ts 同一口径）：三连对正好 3 对、钢板（三同连张）正好 2 个三张；逢人配不能当王。
// 纯函数，可测试；同一手牌 + 级牌的结果有缓存。
import { isJoker, isWild, power, rankName, sortHand } from './cards.ts';
import type { Card } from './cards.ts';

export interface Group {
  label: string;
  cards: Card[];
  /** 逢人配在这一组里当什么牌：卡牌 id → “♠7”“9”等（作者规格第四节：标出逢人配去向） */
  as?: Record<number, string>;
}
export interface Plan { groups: Group[]; score: number; hands: number; note: string; sf: string } // sf：同花顺怎么组（空 = 不凑），用来挑候选；note：给玩家看的方案说明

const SUITS = ['S', 'H', 'C', 'D'] as const;
const SUIT_NAME: Record<string, string> = { S: '黑桃', H: '红桃', C: '梅花', D: '方块' };
const SUIT_MARK: Record<string, string> = { S: '♠', H: '♥', C: '♣', D: '♦' };
const nat = (pos: number) => (pos === 1 ? 14 : pos); // 连牌位置 → 牌点（1 = 当 1 用的 A）
const RANKS = Array.from({ length: 13 }, (_, i) => i + 2); // 2..14
const ix = (rank: number) => rank - 2;

/** 炸弹档位（与 rules.ts bombTier 一致）：4 张 1、5 张 2、同花顺 3、6 张 4 … 10 张 8、四大天王 9。 */
const bombTier = (size: number) => (size <= 5 ? size - 3 : size - 2);
const SF_TIER = 3, JOKER_TIER = 9;

interface SfCand { suit: string; start: number; cards: Card[]; missing: number[] }

/**
 * 在 pool 里找某花色的全部同花顺候选（缺口 ≤ wilds）。noTake：原本 ≥ 4 张的点数（炸弹），这些点数的牌不拿来凑同花顺
 * ——作者规格验收用例“拆炸陷阱：凑同花顺需拆 8888 → 不拆炸”定为硬规则（按规格打分，拆炸常常反而更高分）。
 */
function sfCandidates(pool: Card[], wilds: number, noTake: Set<number>): SfCand[] {
  const out: SfCand[] = [];
  for (const suit of SUITS) {
    for (let start = 1; start <= 10; start++) {
      const used = new Set<number>(), cards: Card[] = [], missing: number[] = [];
      for (let k = 0; k < 5; k++) {
        const r = nat(start + k);
        const c = noTake.has(r) ? undefined : pool.find((x) => x.suit === suit && x.rank === r && !used.has(x.id));
        if (c) { used.add(c.id); cards.push(c); } else missing.push(r);
      }
      if (missing.length <= wilds) out.push({ suit, start, cards, missing });
    }
  }
  return out;
}

// ── 余牌划分：按点数计数，回溯 + 记忆，求代价最小（手数优先，单张、散对为次） ──
type Move = { kind: 'single' | 'pair' | 'triple' | 'fullHouse' | 'straight' | 'tube' | 'plate'; ranks: number[] };
interface Part { cost: number; hands: number; singles: number; loose: number; moves: Move[] }
const COST_HAND = 100, COST_SINGLE = 15, COST_LOOSE = 10;

function partition(counts: number[], memo: Map<string, Part>): Part {
  const key = counts.join(',');
  const hit = memo.get(key);
  if (hit) return hit;
  const i = counts.findIndex((n) => n > 0);
  if (i < 0) { const empty = { cost: 0, hands: 0, singles: 0, loose: 0, moves: [] }; memo.set(key, empty); return empty; }
  let best: Part | null = null;
  const tryMove = (m: Move, take: [number, number][], extra: { singles?: number; loose?: number }) => {
    for (const [r, n] of take) if (counts[ix(r)] < n) return;
    const next = counts.slice();
    for (const [r, n] of take) next[ix(r)] -= n;
    const sub = partition(next, memo);
    const singles = sub.singles + (extra.singles ?? 0), loose = sub.loose + (extra.loose ?? 0);
    const cost = sub.cost + COST_HAND + (extra.singles ?? 0) * COST_SINGLE + (extra.loose ?? 0) * COST_LOOSE;
    if (!best || cost < best.cost) best = { cost, hands: sub.hands + 1, singles, loose, moves: [m, ...sub.moves] };
  };
  const r = i + 2, c = counts[i];
  // 连牌：从最小的这张起，或 A 当 1（只有最小张是 2 时，A2345 / AA2233 / AAA222 才可能）
  const runs: [Move['kind'], number, number][] = [['plate', 2, 3], ['tube', 3, 2], ['straight', 5, 1]];
  for (const [kind, len, width] of runs) {
    for (const start of r === 2 ? [2, 1] : [r]) {
      if (start + len - 1 > 14) continue;
      const ranks = Array.from({ length: len }, (_, k) => nat(start + k));
      tryMove({ kind, ranks }, ranks.map((x) => [x, width]), {});
    }
  }
  if (c >= 3) {
    for (const p of RANKS) if (p !== r && counts[ix(p)] >= 2) tryMove({ kind: 'fullHouse', ranks: [r, p] }, [[r, 3], [p, 2]], {});
    tryMove({ kind: 'triple', ranks: [r] }, [[r, 3]], {});
  }
  if (c >= 2) {
    for (const t of RANKS) if (t !== r && counts[ix(t)] >= 3) tryMove({ kind: 'fullHouse', ranks: [t, r] }, [[t, 3], [r, 2]], {});
    tryMove({ kind: 'pair', ranks: [r] }, [[r, 2]], { loose: 1 });
  }
  tryMove({ kind: 'single', ranks: [r] }, [[r, 1]], { singles: 1 });
  memo.set(key, best!);
  return best!;
}

const LABEL: Record<Move['kind'], string> = { single: '单张', pair: '对子', triple: '三张', fullHouse: '三带对', straight: '顺子', tube: '三连对', plate: '钢板' };

const cache = new Map<string, Plan[]>();

/** 全部候选方案（分数从高到低，只留与最高分相差 ≤ 20 的；至少一个）。 */
export function arrangePlans(hand: Card[], level: number): Plan[] {
  const key = `${level}|${hand.map((c) => c.id).sort((a, b) => a - b).join(',')}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const jokers = hand.filter(isJoker);
  const wilds = hand.filter((c) => isWild(c, level));
  const natural = hand.filter((c) => !isJoker(c) && !isWild(c, level));
  const W = wilds.length;
  const natCount = new Map<number, number>();
  for (const c of natural) natCount.set(c.rank, (natCount.get(c.rank) ?? 0) + 1);
  const memo = new Map<string, Part>();
  const plans: Plan[] = [];

  // 同花顺组合：不凑 / 一个 / 两个互不重叠，总缺口 ≤ 逢人配张数
  const sfSets: SfCand[][] = [[]];
  const bombSet = new Set(RANKS.filter((r) => (natCount.get(r) ?? 0) >= 4));
  for (const a of sfCandidates(natural, W, bombSet)) {
    sfSets.push([a]);
    const rest = natural.filter((c) => !a.cards.includes(c));
    for (const b of sfCandidates(rest, W - a.missing.length, bombSet)) if (b.suit > a.suit || (b.suit === a.suit && b.start > a.start)) sfSets.push([a, b]);
  }

  for (const sfs of sfSets) {
    const usedW = sfs.reduce((n, s) => n + s.missing.length, 0);
    const sfIds = new Set(sfs.flatMap((s) => s.cards.map((c) => c.id)));
    const rest = natural.filter((c) => !sfIds.has(c.id));
    const cnt = new Map<number, number>();
    for (const c of rest) cnt.set(c.rank, (cnt.get(c.rank) ?? 0) + 1);
    const bombRanks = RANKS.filter((r) => (cnt.get(r) ?? 0) >= 4);
    // 炸弹一律保留、不拆（作者规格验收“拆炸陷阱 → 不拆炸，保留 8888”定为硬规则；按规格打分，拆开组顺子常常分更高，与验收矛盾）
    const keepSets: number[][] = [bombRanks];
    const wLeft = W - usedW;
    for (const keep of keepSets) {
      // 剩下的逢人配：不用 / 加到某个点数上成炸弹（原有炸弹扩大，或三张 + 1、对子 + 2 凑成炸弹）
      const wildOpts: [number, number][][] = [[]];
      const canBomb = (r: number, k: number) => (keep.includes(r) || !bombRanks.includes(r)) && (cnt.get(r) ?? 0) + k >= 4;
      for (const r of RANKS) {
        if (wLeft >= 1 && canBomb(r, 1)) wildOpts.push([[r, 1]]);
        if (wLeft >= 2 && canBomb(r, 2)) wildOpts.push([[r, 2]]);
      }
      if (wLeft >= 2) for (const a of RANKS) for (const b of RANKS) if (a < b && canBomb(a, 1) && canBomb(b, 1)) wildOpts.push([[a, 1], [b, 1]]);
      for (const wo of wildOpts) {
        const groups: Group[] = [];
        let tiers = 0, hands = 0;
        const wildPool = wilds.slice();
        if (jokers.length === 4) { groups.push({ label: '四大天王', cards: jokers.slice() }); tiers += JOKER_TIER; hands++; }
        // 炸弹（含逢人配扩大的）
        const bombsHere = new Map<number, number>(); // 点数 → 加几张逢人配
        for (const r of keep) bombsHere.set(r, 0);
        for (const [r, k] of wo) bombsHere.set(r, (bombsHere.get(r) ?? 0) + k);
        const bombGroups: Group[] = [];
        for (const [r, k] of bombsHere) {
          const cs = rest.filter((c) => c.rank === r);
          const ws = wildPool.splice(0, k);
          const g: Group = { label: '炸弹', cards: [...sortHand(cs, level), ...ws] };
          if (ws.length) g.as = Object.fromEntries(ws.map((w) => [w.id, rankName(r)]));
          bombGroups.push(g);
          tiers += bombTier(cs.length + ws.length); hands++;
        }
        bombGroups.sort((a, b) => bombTier(b.cards.length) - bombTier(a.cards.length) || power(b.cards[0].rank, level) - power(a.cards[0].rank, level));
        groups.push(...bombGroups);
        // 同花顺（逢人配补缺口）
        for (const s of sfs) {
          const ws = wildPool.splice(0, s.missing.length);
          const cards: Card[] = [], as: Record<number, string> = {};
          let wi = 0;
          for (let k = 0; k < 5; k++) {
            const r = nat(s.start + k);
            const c = s.cards.find((x) => x.rank === r);
            if (c) cards.push(c); else { const w = ws[wi++]; cards.push(w); as[w.id] = w.rank === r && w.suit === s.suit ? '本色' : `${SUIT_MARK[s.suit]}${rankName(r)}`; }
          }
          groups.push({ label: '同花顺', cards: cards.reverse(), ...(Object.keys(as).length ? { as } : {}) }); // 大的在上
          tiers += SF_TIER; hands++;
        }
        if (wildPool.length) groups.push({ label: '逢人配', cards: wildPool.slice() }); // 没用上的逢人配单独放，不算手数（出牌时可以补到任何牌型里）
        // 余牌划分
        const inBomb = new Set(bombGroups.flatMap((g) => g.cards.map((c) => c.id)));
        const left = rest.filter((c) => !inBomb.has(c.id));
        const counts = RANKS.map((r) => left.filter((c) => c.rank === r).length);
        const part = partition(counts, memo);
        const byRank = new Map<number, Card[]>();
        for (const c of sortHand(left, level)) byRank.set(c.rank, [...(byRank.get(c.rank) ?? []), c]);
        const take = (r: number, n: number) => byRank.get(r)!.splice(0, n);
        const whole: Group[] = [], loose: Card[] = [], singles: Card[] = [];
        for (const mv of part.moves) {
          if (mv.kind === 'single') singles.push(...take(mv.ranks[0], 1));
          else if (mv.kind === 'pair') loose.push(...take(mv.ranks[0], 2));
          else if (mv.kind === 'triple') whole.push({ label: '三张', cards: take(mv.ranks[0], 3) });
          else if (mv.kind === 'fullHouse') whole.push({ label: '三带对', cards: [...take(mv.ranks[0], 3), ...take(mv.ranks[1], 2)] });
          else { const w = mv.kind === 'plate' ? 3 : mv.kind === 'tube' ? 2 : 1; whole.push({ label: LABEL[mv.kind], cards: [...mv.ranks].reverse().flatMap((x) => take(x, w)) }); }
        }
        // 王：一对同色王算对子，其余单张
        const jk = { small: jokers.filter((j) => j.rank === 16), big: jokers.filter((j) => j.rank === 17) };
        let jokerHands = 0, jokerSingles = 0;
        if (jokers.length !== 4) for (const g of [jk.big, jk.small]) { if (g.length === 2) { loose.push(...g); jokerHands++; } else if (g.length === 1) { singles.push(g[0]); jokerHands++; jokerSingles++; } }
        const order: Record<string, number> = { 钢板: 0, 三连对: 1, 顺子: 2, 三带对: 3, 三张: 4 };
        whole.sort((a, b) => order[a.label] - order[b.label] || power(b.cards[0].rank, level) - power(a.cards[0].rank, level));
        groups.push(...whole);
        if (loose.length) groups.push({ label: '对子', cards: sortHand(loose, level) });
        if (singles.length) groups.push({ label: '单张', cards: sortHand(singles, level) });
        hands += part.hands + jokerHands;
        const control = [...whole, ...(loose.length ? [{ cards: loose }] : []), ...(singles.length ? [{ cards: singles }] : [])]
          .flatMap((g) => g.cards).filter((c) => isJoker(c) || c.rank === level || c.rank === 14).length;
        const breaks = bombRanks.filter((r) => !keep.includes(r)).length; // 拆炸：炸弹点数拆开去组顺子等（同花顺不许拆炸，见 sfCandidates）
        const score = -100 * hands + 60 * tiers - 15 * (part.singles + jokerSingles) - 10 * part.loose + 5 * control - 8 * breaks;
        // 方案说明（作者 11-03：两个方案都写“不凑同花顺”分不清）：写同花顺怎么组 + 逢人配用在哪
        const sfNote = sfs.map((s) => `${SUIT_NAME[s.suit]}同花顺 ${rankName(nat(s.start))}-${rankName(nat(s.start + 4))}`).join(' + ');
        const wildNote = W === 0 ? '' : [...wo.map(([r, k]) => `逢人配补成${(rest.filter((c) => c.rank === r).length + k)}张炸 ${rankName(r)}`), ...(wildPool.length ? ['逢人配留着不用'] : [])].join('，');
        const note = [sfNote || '不凑同花顺', wildNote].filter(Boolean).join('；');
        plans.push({ groups, score, hands, note, sf: sfNote });
      }
    }
  }
  plans.sort((a, b) => b.score - a.score);
  // 候选方案（规格第三节第 5 步 + 第四节第 2 条）：① 与最高分相差 ≤ 20 的；② 同花顺有多种组法（不同花色 / 窗口）时，每种组法各留一个最好的，
  // 让玩家在同花顺那组上切换。去重：同样的分组只留一个；最多 4 个。
  const seen = new Set<string>(), notes = new Set<string>(), out: Plan[] = [];
  const hasSf = plans.some((p) => p.sf);
  for (const p of plans) {
    const sig = p.groups.map((g) => `${g.label}:${g.cards.map((c) => c.id).sort((a, b) => a - b).join('.')}`).sort().join('|');
    if (seen.has(sig)) continue;
    seen.add(sig);
    const near = !out.length || out[0].score - p.score <= 20;
    const newSf = hasSf && !!p.sf && !notes.has(p.sf);
    if ((near || newSf) && out.length < 4) { out.push(p); if (p.sf) notes.add(p.sf); }
  }
  if (cache.size > 200) cache.clear();
  cache.set(key, out);
  return out;
}

/** 默认方案的分组（界面与新手提示用）。 */
export function arrangeCombos(hand: Card[], level: number): Group[] {
  return arrangePlans(hand, level)[0].groups;
}
